// CRITICAL: Import layer order declaration FIRST before any other CSS
import "./main.css";
import { AnalyticsProvider } from "@analytics/client";
import { ErrorBoundary } from "@error-tracking/client";
import { loadRemote } from "@module-federation/enhanced/runtime";
import {
  AuthProvider,
  ImpersonationProvider,
  initializeAuth,
  getKeycloakInstance,
} from "@spiriidk/auth";
import {
  FEATURE_FLAGS,
  FEATURE_FLAGS_CONFIG,
  DEFAULT_LOCALE,
  normalizeLocale,
  LOCALE_STORAGE_KEY,
} from "@spiriidk/connect-configs";
import {
  FeatureFlagProvider,
  getFlag,
  initializeFeatureFlags,
  setContext,
} from "@spiriidk/feature-flags";
import { useLocalStorageSync } from "@spiriidk/utils";
import { QueryClientProvider } from "@tanstack/react-query";
import React, { StrictMode, Suspense, use, useEffect } from "react";
import { createRoot } from "react-dom/client";
import {
  addErrorHandler,
  getAppStatus,
  LOAD_ERROR,
  registerApplication,
  start,
} from "single-spa";

import { isConnectRoute, isTechUiRoute } from "~/utils/routes";

import { App } from "./app";
import { AppErrorFallback, FullPageLoader } from "./components";
import { analyticsClient, loadCatalog, queryClient } from "./configs";
import { BetaFeaturesProvider } from "./features/navigation/contexts";
import { useEffectOnce } from "./hooks";
import { enableMocking, mockHandlerRegistry } from "./mocks";

interface InitializationData {
  authData: Awaited<ReturnType<typeof initializeAuth>>;
}

let initializationPromise: Promise<InitializationData> | null = null;

function getInitializationPromise(): Promise<InitializationData> {
  if (!initializationPromise) {
    initializationPromise = initializeApp();
  }

  return initializationPromise;
}

async function initializeApp(): Promise<InitializationData> {
  // Get initial locale from localStorage
  const storedLocale =
    localStorage.getItem(LOCALE_STORAGE_KEY) || DEFAULT_LOCALE;
  const initialLocale = normalizeLocale(storedLocale);

  // Set initial HTML lang attribute
  document.documentElement.lang = initialLocale;

  await loadCatalog(initialLocale);

  // Initialize MSW early, before registering micro-frontends
  await enableMocking().catch((error) => {
    console.error("Failed to enable mocking:", error);
  });

  // TODO: move to env
  const authData = await initializeAuth(
    {
      url: import.meta.env.VITE_AUTH_SERVER_URL as string,
      realm: "production",
      clientId: "techportal",
    },
    {
      onLoad: "check-sso",
      pkceMethod: "S256",
      checkLoginIframe: true,
      silentCheckSsoRedirectUri: `${window.location.origin}/silent-check-sso.html`,
    }
  );

  await analyticsClient.identify(authData.login, {
    role: authData.userPermissions[0],
    operatorId: authData.permittedOperatorId,
    companyId: authData.permittedCompanyId,
  });

  await initializeFeatureFlags({
    clientSideId: import.meta.env.VITE_LAUNCHDARKLY_CLIENT_ID as
      | string
      | undefined,
    flags: FEATURE_FLAGS_CONFIG,
    options: {
      streaming: true,
    },
  });

  await setContext({
    targetingKey: authData.login,
    operatorId: authData.permittedOperatorId,
    companyId: authData.permittedCompanyId,
    permissions: authData.userPermissions,
  });

  addErrorHandler((err) => {
    if (getAppStatus(err.appOrParcelName) === LOAD_ERROR) {
      console.error(
        "Failed to load micro-frontend:",
        err.appOrParcelName,
        err.message
      );
    }
  });

  const isSpiriiUIEnabled = getFlag(FEATURE_FLAGS.SPIRII_UI);

  if (isSpiriiUIEnabled) {
    // Register connect app's mock handlers if mocking is enabled
    if (import.meta.env.VITE_MOCK_API === "true") {
      try {
        interface ConnectMockModule {
          registerMockHandlers?: () => Promise<
            ((registry: typeof mockHandlerRegistry) => void) | undefined
          >;
        }

        const connectApp =
          await loadRemote<ConnectMockModule>("spirii_connect/spa");

        if (connectApp?.registerMockHandlers) {
          const registerFn = await connectApp.registerMockHandlers();
          if (registerFn) {
            registerFn(mockHandlerRegistry);

            // Re-apply handlers to the running worker
            const { worker } = await import("./mocks");
            worker.use(...mockHandlerRegistry.getHandlers());
          }
        }
      } catch (error) {
        console.error("Failed to register connect mock handlers:", error);
      }
    }

    registerApplication(
      "@spiriidk/connect",
      () =>
        loadRemote("spirii_connect/spa") as Promise<
          import("single-spa").LifeCycles
        >,
      (location) => isConnectRoute(location.pathname),
      { kcInstance: getKeycloakInstance() }
    );
  }

  // tech-ui is now an MF remote — load via Module Federation
  registerApplication(
    "@spiriidk/tech-ui",
    () =>
      loadRemote("spirii_tech_ui/spa") as Promise<
        import("single-spa").LifeCycles
      >,
    (location) => {
      const pathname = location.pathname;
      const techUiOwnsRoute = isTechUiRoute(pathname);
      const connectOwnsRoute = isSpiriiUIEnabled && isConnectRoute(pathname);
      return techUiOwnsRoute && !connectOwnsRoute;
    },
    // Need to pass Keycloak Instance in order to not refactor the current auth flow in tech-ui
    { kcInstance: getKeycloakInstance() }
  );

  return { authData };
}

export function AppInitializer() {
  use(getInitializationPromise());

  // Listen to locale changes and reload catalog
  const [currentLocale] = useLocalStorageSync({
    key: LOCALE_STORAGE_KEY,
    defaultValue: DEFAULT_LOCALE,
  });

  const locale = normalizeLocale(currentLocale);

  useEffect(() => {
    // Update HTML lang attribute for accessibility and SEO
    document.documentElement.lang = locale;

    loadCatalog(locale).catch((err) => {
      console.error("Failed to load catalog:", err);
    });
  }, [locale]);

  // We need to start single-spa after the app is initialized, in order to have all the micro-frontend elements in place
  useEffectOnce(() => {
    start();
  });

  return (
    <FeatureFlagProvider>
      <AnalyticsProvider instance={analyticsClient}>
        <AuthProvider>
          <ImpersonationProvider>
            <BetaFeaturesProvider>
              <QueryClientProvider client={queryClient}>
                <App />
              </QueryClientProvider>
            </BetaFeaturesProvider>
          </ImpersonationProvider>
        </AuthProvider>
      </AnalyticsProvider>
    </FeatureFlagProvider>
  );
}

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <ErrorBoundary
      fallback={({ error }) => (
        <AppErrorFallback
          error={error}
          reset={() => window.location.reload()}
        />
      )}
    >
      <Suspense fallback={<FullPageLoader />}>
        <AppInitializer />
      </Suspense>
    </ErrorBoundary>
  </StrictMode>
);
